import { supabase, isSupabaseConfigured } from '@/lib/supabase';

export interface ResendConfig {
  enabled: boolean;
}

export interface OrderNotificationItem {
  productName: string;
  sizeRange: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

export interface OrderNotificationInput {
  orderNumber: string;
  customerEmail: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  customerCity: string;
  total: number;
  paymentMethod: string;
  production: boolean;
  items: OrderNotificationItem[];
}

export interface NotificationResult {
  sent: boolean;
  reason?: string;
}

// Lee solo el interruptor "confirmación por correo" (dato no sensible).
// La presencia real de las llaves de Resend se verifica aparte con
// `checkResendConfigured` (via Edge Function), porque los secrets viven en
// Supabase y el navegador no puede leerlos.
export async function getResendConfig(): Promise<ResendConfig> {
  if (!isSupabaseConfigured || !supabase) return { enabled: false };
  const { data, error } = await supabase
    .from('company_settings_public')
    .select('enable_order_email')
    .limit(1)
    .maybeSingle();
  if (error || !data) return { enabled: false };
  const row = data as Record<string, any>;
  return { enabled: Boolean(row.enable_order_email) };
}

// Verifica si RESEND_API_KEY y RESEND_FROM_DOMAIN están presentes en los secrets
// de Supabase. Devuelve un booleano (nunca los valores).
export async function checkResendConfigured(): Promise<boolean> {
  if (!isSupabaseConfigured || !supabase) return false;
  const { data, error } = await supabase.functions.invoke('send-order-email', {
    body: { check: true },
  });
  if (error) return false;
  return Boolean((data as { configured?: boolean } | null)?.configured);
}

// Dispara la confirmación de pedido por correo a través del Edge Function
// `send-order-email` (que corre con service role y envía vía Resend usando la
// llave guardada en los secrets de Supabase, nunca en el navegador).
// Devuelve `sent: false` (sin lanzar error) si el envío está desactivado o el
// pedido no tiene correo de cliente.
export async function sendOrderConfirmation(
  input: OrderNotificationInput,
): Promise<NotificationResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { sent: false, reason: 'Supabase no está configurado' };
  }
  if (!input.customerEmail) {
    return { sent: false, reason: 'El pedido no tiene correo de cliente' };
  }
  // El envío solo procede si el admin activó la confirmación por correo.
  // La validación de que Resend esté configurado la hace la propia función.
  const cfg = await getResendConfig();
  if (!cfg.enabled) {
    return { sent: false, reason: 'La confirmación por correo está desactivada' };
  }
  const { data, error } = await supabase.functions.invoke('send-order-email', {
    body: input,
  });
  if (error) {
    return { sent: false, reason: error.message ?? 'No se pudo enviar el correo' };
  }
  const result = (data ?? {}) as NotificationResult;
  return { sent: Boolean(result.sent), reason: result.reason };
}

// Envía el correo de confirmación de un pedido pagado en línea (ePayco). A
// diferencia de `sendOrderConfirmation`, no recibe los datos del cliente:
// el Edge Function busca el pedido por número y reconstruye todo desde la BD.
// Se dispara ÚNICAMENTE cuando el pago queda aprobado (no al confirmar el
// pedido). El método de pago siempre se reporta como 'epayco'.
export async function sendOnlinePaymentApprovalEmail(
  orderNumber: string,
): Promise<NotificationResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { sent: false, reason: 'Supabase no está configurado' };
  }
  const cfg = await getResendConfig();
  if (!cfg.enabled) {
    return { sent: false, reason: 'La confirmación por correo está desactivada' };
  }
  const { data, error } = await supabase.functions.invoke('send-order-email', {
    body: { orderNumber },
  });
  if (error) {
    return { sent: false, reason: error.message ?? 'No se pudo enviar el correo' };
  }
  const result = (data ?? {}) as NotificationResult;
  return { sent: Boolean(result.sent), reason: result.reason };
}